import { useState, useRef, useEffect } from "react";
import styles from "./JobForm.module.css";
import { ENTITY_ACTIVE, MAX_PAY_RATE, MAX_OFFICER_SLOTS, MAX_RECURRING_DAYS, MAX_TITLE_LENGTH } from "../constants";
import { validateFile } from "../utils/fileUpload";

function generateRecurringDates(startDate: string, frequency: string, days: number[], endDate: string): string[] {
  const dates: string[] = [];
  const start = new Date(startDate + "T00:00:00");
  const end = new Date(endDate + "T23:59:59");
  if (start > end) return [];

  if (frequency === "daily") {
    const d = new Date(start);
    while (d <= end) {
      dates.push(d.toISOString().split("T")[0]);
      d.setDate(d.getDate() + 1);
    }
  } else if (frequency === "weekly") {
    if (days.length === 0) return [];
    const d = new Date(start);
    while (d <= end) {
      if (days.includes(d.getDay())) {
        dates.push(d.toISOString().split("T")[0]);
      }
      d.setDate(d.getDate() + 1);
    }
  } else if (frequency === "biweekly") {
    if (days.length === 0) return [];
    const d = new Date(start);
    let weekNum = 0;
    let lastSunday = new Date(d);
    lastSunday.setDate(lastSunday.getDate() - lastSunday.getDay());
    while (d <= end) {
      const currentSunday = new Date(d);
      currentSunday.setDate(currentSunday.getDate() - currentSunday.getDay());
      if (currentSunday.getTime() !== lastSunday.getTime()) {
        weekNum++;
        lastSunday = currentSunday;
      }
      if (weekNum % 2 === 0 && days.includes(d.getDay())) {
        dates.push(d.toISOString().split("T")[0]);
      }
      d.setDate(d.getDate() + 1);
    }
  } else if (frequency === "monthly") {
    const d = new Date(start);
    while (d <= end) {
      dates.push(d.toISOString().split("T")[0]);
      d.setMonth(d.getMonth() + 1);
    }
  }
  return dates;
}

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];


function normalizeContacts(poc: any): { name: string; phone: string; email: string }[] {
  if (Array.isArray(poc) && poc.length > 0) return poc;
  if (typeof poc === "string" && poc.trim()) return [{ name: poc.trim(), phone: "", email: "" }];
  return [{ name: "", phone: "", email: "" }];
}

function buildFormFromSource(source: any) {
  if (!source) {
    return {
      title: "",
      entity: "",
      date: "",
      startTime: "",
      endTime: "",
      location: "",
      payRate: "",
      slotsTotal: "",
      pointOfContact: [{ name: "", phone: "", email: "" }],
      details: "",
      isPrivate: false,
    };
  }
  return {
    title: source.title || "",
    entity: source.entity || "",
    date: source.date || "",
    startTime: source.startTime || "",
    endTime: source.endTime || "",
    location: source.location || "",
    payRate: source.payRate || "",
    slotsTotal: source.slotsTotal || "",
    pointOfContact: normalizeContacts(source.pointOfContact),
    details: source.details || "",
    isPrivate: source.isPrivate || false,
  };
}

export default function JobForm({ entities, tiers, editingJob = null, initialData = null, onSubmit, onCancel }: any) {
  // Initialize the form synchronously from editingJob/initialData on first
  // render so consumers (and tests) see the pre-filled values immediately.
  // The effects below handle subsequent prop changes via a deferred microtask.
  const [form, setForm] = useState<any>(() => buildFormFromSource(editingJob || initialData));

  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [rateTier, setRateTier] = useState(() => {
    const source = editingJob || initialData;
    if (source?.payRate && tiers) {
      const matchingTier = tiers.find((t: any) => Number(t.rate) === Number(source.payRate));
      if (matchingTier) return matchingTier.id;
    }
    return "custom";
  });
  const [pendingFiles, setPendingFiles] = useState<any[]>([]);
  const [fileError, setFileError] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [recurring, setRecurring] = useState(false);
  const [frequency, setFrequency] = useState("weekly");
  const [recurDays, setRecurDays] = useState<number[]>([]);
  const [recurEnd, setRecurEnd] = useState("");

  // Re-sync form when editingJob prop changes after mount. Defer to a
  // microtask so setState runs in a callback, not the effect body.
  useEffect(() => {
    if (!editingJob) return;
    Promise.resolve().then(() => {
      setForm(buildFormFromSource(editingJob));
      if (editingJob.payRate) {
        const matchingTier = tiers.find((t: any) => Number(t.rate) === Number(editingJob.payRate));
        setRateTier(matchingTier ? matchingTier.id : "custom");
      }
    });
  }, [editingJob, tiers]);

  // Re-sync from initialData (Convert to Job flow) after mount. Same defer.
  useEffect(() => {
    if (!initialData || editingJob) return;
    Promise.resolve().then(() => {
      setForm(buildFormFromSource(initialData));
    });
  }, [initialData, editingJob]);

  const activeEntities = entities.filter((e: any) => e.status === ENTITY_ACTIVE);

  const handleEntityChange = (entityName: string) => {
    const entity = activeEntities.find((e: any) => e.name === entityName);
    if (entity?.defaultPayRate) {
      const matchingTier = tiers.find((t: any) => Number(t.rate) === Number(entity.defaultPayRate));
      setRateTier(matchingTier ? matchingTier.id : "custom");
    }
    setForm((prev: any) => ({
      ...prev,
      entity: entityName,
      payRate: entity?.defaultPayRate || prev.payRate,
      pointOfContact: entity?.defaultPointOfContact
        ? normalizeContacts(entity.defaultPointOfContact)
        : prev.pointOfContact,
      location: prev.location || "",
    }));
  };

  const updateContact = (index: number, field: string, value: string) => {
    setForm((prev: any) => {
      const updated = prev.pointOfContact.map((c: any, i: number) =>
        i === index ? { ...c, [field]: value } : c
      );
      return { ...prev, pointOfContact: updated };
    });
  };

  const addContact = () => {
    setForm((prev: any) => ({
      ...prev,
      pointOfContact: [...prev.pointOfContact, { name: "", phone: "", email: "" }],
    }));
  };

  const removeContact = (index: number) => {
    setForm((prev: any) => ({
      ...prev,
      pointOfContact: prev.pointOfContact.filter((_: any, i: number) => i !== index),
    }));
  };

  const handleFileAdd = (e: any) => {
    const files = Array.from(e.target.files || []) as File[];
    setFileError("");
    for (const file of files) {
      const err = validateFile(file);
      if (err) {
        setFileError(err);
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
    }
    setPendingFiles((prev) => [...prev, ...files]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleDay = (day: number) => {
    setRecurDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const previewDates = recurring && form.date && recurEnd
    ? generateRecurringDates(form.date, frequency, recurDays, recurEnd)
    : [];

  const validate = () => {
    const errs: Record<string, string> = {};
    const rate = Number(form.payRate);
    const slots = Number(form.slotsTotal);

    if (!form.title.trim()) {
      errs.title = "Job title is required";
    } else if (form.title.trim().length > MAX_TITLE_LENGTH) {
      errs.title = `Title cannot exceed ${MAX_TITLE_LENGTH} characters`;
    }

    if (isNaN(rate) || rate <= 0) {
      errs.payRate = "Pay rate must be greater than $0";
    } else if (!/^\d+(\.\d{1,2})?$/.test(String(form.payRate).trim())) {
      errs.payRate = "Pay rate can have at most 2 decimal places";
    } else if (rate > MAX_PAY_RATE) {
      errs.payRate = `Pay rate cannot exceed $${MAX_PAY_RATE.toLocaleString()}/hr`;
    }

    if (isNaN(slots) || !Number.isInteger(slots) || slots < 1) {
      errs.slotsTotal = "Officers needed must be a whole number (1 or more)";
    } else if (slots > MAX_OFFICER_SLOTS) {
      errs.slotsTotal = `Officers needed cannot exceed ${MAX_OFFICER_SLOTS}`;
    }

    if (form.startTime && form.endTime && form.endTime <= form.startTime) {
      errs.endTime = "End time must be after start time";
    }

    if (recurring && form.date && recurEnd) {
      if (recurEnd < form.date) {
        errs.recurEnd = "End date must be on or after the start date";
      } else {
        const start = new Date(form.date);
        const end = new Date(recurEnd);
        const daysDiff = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
        if (daysDiff > MAX_RECURRING_DAYS) {
          errs.recurEnd = `Recurring range cannot exceed ${MAX_RECURRING_DAYS} days`;
        }
      }
    }

    return errs;
  };

  const errorSummaryRef = useRef<HTMLDivElement | null>(null);

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (submitting) return;
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      setTimeout(() => errorSummaryRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 0);
      return;
    }

    setSubmitError("");
    setSubmitting(true);
    try {
      if (recurring && !editingJob) {
        await onSubmit(form, { recurring: true, frequency, days: recurDays, endDate: recurEnd, dates: previewDates }, pendingFiles);
      } else {
        await onSubmit(form, null, pendingFiles);
      }
    } catch (err: any) {
      setSubmitError(err?.message || "Failed to save job. Please try again.");
      setSubmitting(false);
      setTimeout(() => errorSummaryRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 0);
    }
  };

  const set = (field: string) => (e: any) => setForm((prev: any) => ({ ...prev, [field]: e.target.value }));
  const showDays = recurring && (frequency === "weekly" || frequency === "biweekly");

  return (
    <div>
      <h2 className={styles.heading}>{editingJob ? "Edit Job" : "Post New Job"}</h2>
      <form onSubmit={handleSubmit} className={styles.form} aria-label={editingJob ? "Edit job form" : "Post new job form"}>
        {Object.keys(errors).length > 0 && (
          <div ref={errorSummaryRef} className={styles.errorSummary} role="alert">
            <strong className={styles.errorSummaryTitle}>Please fix the following errors:</strong>
            <ul className={styles.errorSummaryList}>
              {errors.title && <li>{errors.title}</li>}
              {errors.payRate && <li>{errors.payRate}</li>}
              {errors.slotsTotal && <li>{errors.slotsTotal}</li>}
              {errors.endTime && <li>{errors.endTime}</li>}
              {errors.recurEnd && <li>{errors.recurEnd}</li>}
            </ul>
          </div>
        )}
        {submitError && Object.keys(errors).length === 0 && (
          <div ref={errorSummaryRef} className={styles.errorSummary} role="alert">
            <strong className={styles.errorSummaryTitle}>Could not save job:</strong>
            <p style={{ margin: 0 }}>{submitError}</p>
          </div>
        )}
        <label className={styles.label} htmlFor="jf-entity">
          Entity
          <input
            id="jf-entity"
            className={styles.input}
            list="entity-options"
            value={form.entity}
            onChange={(e) => handleEntityChange(e.target.value)}
            placeholder="Select or type an entity name"
            autoComplete="off"
          />
          <datalist id="entity-options">
            {activeEntities.map((e) => (
              <option key={e.id} value={e.name} />
            ))}
          </datalist>
        </label>

        <label className={styles.label} htmlFor="jf-title">
          Job Title
          <input id="jf-title" className={styles.input} type="text" value={form.title} onChange={set("title")} required />
        </label>

        <div className={styles.row}>
          <label className={`${styles.label} ${styles.flexOne}`} htmlFor="jf-date">
            {recurring ? "Start Date" : "Date"}
            <input id="jf-date" className={styles.input} type="date" value={form.date} onChange={set("date")} required />
          </label>
          <label className={`${styles.label} ${styles.flexOne}`} htmlFor="jf-start">
            Start Time
            <input id="jf-start" className={styles.input} type="time" value={form.startTime} onChange={set("startTime")} required />
          </label>
          <label className={`${styles.label} ${styles.flexOne}`} htmlFor="jf-end">
            End Time
            <input id="jf-end" className={`${styles.input} ${errors.endTime ? styles.inputError : ""}`} type="time" value={form.endTime} onChange={(e) => { set("endTime")(e); setErrors((prev) => ({ ...prev, endTime: undefined })); }} required />
            {errors.endTime && <span className={styles.errorMsg}>{errors.endTime}</span>}
          </label>
        </div>

        {!editingJob && (
          <div className={styles.recurSection}>
            <label className={styles.checkLabel}>
              <input
                type="checkbox"
                checked={recurring}
                onChange={(e) => setRecurring(e.target.checked)}
              />
              <span>Recurring job</span>
            </label>

            {recurring && (
              <div className={styles.recurOptions}>
                <div className={styles.row}>
                  <label className={`${styles.label} ${styles.flexOne}`} htmlFor="jf-freq">
                    Frequency
                    <select id="jf-freq" className={styles.input} value={frequency} onChange={(e) => setFrequency(e.target.value)}>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Every 2 Weeks</option>
                      <option value="monthly">Monthly (same date)</option>
                    </select>
                  </label>
                  <label className={`${styles.label} ${styles.flexOne}`} htmlFor="jf-recurend">
                    End Date
                    <input
                      id="jf-recurend"
                      className={`${styles.input} ${errors.recurEnd ? styles.inputError : ""}`}
                      type="date"
                      value={recurEnd}
                      onChange={(e) => { setRecurEnd(e.target.value); setErrors((prev) => ({ ...prev, recurEnd: undefined })); }}
                      min={form.date || undefined}
                      required={recurring}
                    />
                    {errors.recurEnd && <span className={styles.errorMsg}>{errors.recurEnd}</span>}
                  </label>
                </div>

                {showDays && (
                  <div className={styles.dayPicker}>
                    <span className={styles.dayPickerLabel}>Days of week</span>
                    <div className={styles.dayBtns}>
                      {DAY_LABELS.map((label, i) => (
                        <button
                          key={i}
                          type="button"
                          className={`${styles.dayBtn} ${recurDays.includes(i) ? styles.dayBtnActive : ""}`}
                          onClick={() => toggleDay(i)}
                          aria-pressed={recurDays.includes(i)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {previewDates.length > 0 && (
                  <div className={styles.preview}>
                    <span className={styles.previewLabel}>
                      {previewDates.length} job{previewDates.length !== 1 ? "s" : ""} will be created
                      {" "}&mdash; {frequency}{showDays && recurDays.length > 0 ? ` on ${recurDays.map(d => DAY_LABELS[d]).join(", ")}` : ""}
                    </span>
                    <div className={styles.previewDates}>
                      {previewDates.slice(0, 12).map((d) => {
                        const date = new Date(d + "T00:00:00");
                        const dayName = date.toLocaleDateString("en-US", { weekday: "short" });
                        return <span key={d} className={styles.previewDate}>{dayName} {d}</span>;
                      })}
                      {previewDates.length > 12 && (
                        <span className={styles.previewMore}>+{previewDates.length - 12} more</span>
                      )}
                    </div>
                  </div>
                )}
                {recurring && form.date && !recurEnd && (
                  <p className={styles.privateHint}>Select an end date to see how many jobs will be created.</p>
                )}
              </div>
            )}
          </div>
        )}

        <label className={styles.label} htmlFor="jf-location">
          Location
          <input id="jf-location" className={styles.input} type="text" value={form.location} onChange={set("location")} required />
        </label>

        <div className={styles.label}>
          Pay Rate ($/hr)
          {tiers.length > 0 && (
            <div className={styles.tierBtns}>
              {tiers.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`${styles.tierBtn} ${rateTier === t.id ? styles.tierBtnActive : ""}`}
                  onClick={() => {
                    setRateTier(t.id);
                    setForm((prev) => ({ ...prev, payRate: t.rate }));
                    setErrors((prev) => ({ ...prev, payRate: undefined }));
                  }}
                >
                  {t.name} — ${t.rate}/hr
                </button>
              ))}
              <button
                type="button"
                className={`${styles.tierBtn} ${rateTier === "custom" ? styles.tierBtnActive : ""}`}
                onClick={() => {
                  setRateTier("custom");
                  setForm((prev) => ({ ...prev, payRate: "" }));
                }}
              >
                Custom
              </button>
            </div>
          )}
          {(tiers.length === 0 || rateTier === "custom") && (
            <input id="jf-rate" aria-label="Pay rate" className={`${styles.input} ${errors.payRate ? styles.inputError : ""}`} type="number" value={form.payRate} onChange={(e) => { set("payRate")(e); setErrors((prev) => ({ ...prev, payRate: undefined })); }} required min="0.01" max="10000" step="0.01" placeholder="Enter rate" />
          )}
          {errors.payRate && <span className={styles.errorMsg}>{errors.payRate}</span>}
        </div>

        <label className={styles.label} htmlFor="jf-slots">
          Officers Needed
          <input id="jf-slots" className={`${styles.input} ${errors.slotsTotal ? styles.inputError : ""}`} type="number" value={form.slotsTotal} onChange={(e) => { set("slotsTotal")(e); setErrors((prev) => ({ ...prev, slotsTotal: undefined })); }} required min="1" max="999" step="1" />
          {errors.slotsTotal && <span className={styles.errorMsg}>{errors.slotsTotal}</span>}
        </label>

        <fieldset className={styles.contactFieldset}>
          <legend className={styles.contactLegend}>Points of Contact</legend>
          {form.pointOfContact.map((contact, i) => (
            <div key={i} className={styles.contactEntry}>
              <div className={styles.contactFields}>
                <input
                  className={styles.input}
                  type="text"
                  placeholder="Name"
                  aria-label={`Contact ${i + 1} name`}
                  value={contact.name}
                  onChange={(e) => updateContact(i, "name", e.target.value)}
                />
                <input
                  className={styles.input}
                  type="tel"
                  placeholder="Phone"
                  aria-label={`Contact ${i + 1} phone`}
                  value={contact.phone}
                  onChange={(e) => updateContact(i, "phone", e.target.value)}
                />
                <input
                  className={styles.input}
                  type="email"
                  placeholder="Email"
                  aria-label={`Contact ${i + 1} email`}
                  value={contact.email}
                  onChange={(e) => updateContact(i, "email", e.target.value)}
                />
              </div>
              {form.pointOfContact.length > 1 && (
                <button
                  type="button"
                  className={styles.removeContactBtn}
                  onClick={() => removeContact(i)}
                  aria-label={`Remove contact ${i + 1}`}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          <button type="button" className={styles.addContactBtn} onClick={addContact}>
            + Add Contact
          </button>
        </fieldset>

        <label className={styles.label} htmlFor="jf-details">
          Details / Notes
          <textarea id="jf-details" className={styles.textarea} value={form.details} onChange={set("details")} />
        </label>

        <div className={styles.attachSection}>
          <span className={styles.label} style={{ marginBottom: 0 }}>Attachments</span>
          <input
            type="file"
            ref={fileInputRef}
            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.txt"
            style={{ display: "none" }}
            onChange={handleFileAdd}
            multiple
          />
          {pendingFiles.length > 0 && (
            <div className={styles.fileList}>
              {pendingFiles.map((file, i) => (
                <div key={i} className={styles.fileItem}>
                  <span className={styles.fileName}>{file.name}</span>
                  <span className={styles.fileSize}>
                    {file.size < 1024 ? `${file.size} B` : file.size < 1048576 ? `${Math.round(file.size / 1024)} KB` : `${(file.size / 1048576).toFixed(1)} MB`}
                  </span>
                  <button type="button" className={styles.removeContactBtn} onClick={() => removeFile(i)}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
          {fileError && <span className={styles.errorMsg}>{fileError}</span>}
          <button type="button" className={styles.addContactBtn} onClick={() => fileInputRef.current?.click()}>
            + Add File
          </button>
          <span className={styles.privateHint}>PDF, images, Word docs, text files. Max 10 MB each.</span>
        </div>

        <div className={styles.recurSection}>
          <label className={styles.checkLabel}>
            <input
              type="checkbox"
              checked={form.isPrivate}
              onChange={(e) => setForm((prev) => ({ ...prev, isPrivate: e.target.checked }))}
            />
            <span>Private job</span>
          </label>
          {form.isPrivate && (
            <p className={styles.privateHint}>
              Private jobs are hidden from officers. Only admins can see them and assign officers directly.
            </p>
          )}
        </div>

        <div className={styles.actions}>
          <button type="submit" className={styles.submitBtn} disabled={submitting}>
            {submitting
              ? (editingJob ? "Saving..." : "Posting...")
              : (editingJob ? "Save Changes" : recurring ? `Post ${previewDates.length || ""} Job${previewDates.length !== 1 ? "s" : ""}` : "Post Job")}
          </button>
          <button type="button" className={styles.cancelBtn} onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
